import React from 'react'

import {IconButton} from '../index.js'
import {tableColors, tablePagination} from './styles.js'
import t from '../../../helpers/Languages.js'

interface TablePaginationProps {
    page: number
    nrOfPages: number
    onSelectPage: (page: number) => void
}

// Pages shown at each side of the current page
const pagesAround = 4

/** First/previous, up to 9 page numbers around the current page, next/last */
const TablePagination = ({page, nrOfPages, onSelectPage}: TablePaginationProps) => {
    const items = []

    let pagesLeft = Math.min(pagesAround, Math.max(0, pagesAround - (pagesAround + 1 - page)))
    let pagesRight = Math.min(pagesAround, nrOfPages - page)

    if (pagesLeft < pagesAround) {
        pagesRight += pagesAround - pagesLeft
    } else if (pagesRight < pagesAround) {
        pagesLeft += pagesAround - pagesRight
    }

    const pageButton = (pageNr: number) => (
        <IconButton
            key={`page${pageNr}`}
            style={{fontSize: tablePagination.pageFontSize}}
            aria-label={t('tablePage', {page: pageNr})}
            onClick={() => onSelectPage(pageNr)}
        >
            {pageNr}
        </IconButton>
    )

    for (let number = 0; number < pagesLeft; number++) {
        const pageNr = page - pagesLeft + number
        if (pageNr > 0) {
            items.push(pageButton(pageNr))
        }
    }

    items.push(
        <IconButton
            key={`page${page}`}
            style={{color: tableColors.activePage, fontSize: tablePagination.activePageFontSize, fontWeight: 'bold'}}
            aria-label={t('tablePage', {page})}
            aria-current='page'
        >
            {page}
        </IconButton>
    )

    for (let number = 1; number <= pagesRight; number++) {
        const pageNr = page + number
        if (pageNr <= nrOfPages) {
            items.push(pageButton(pageNr))
        }
    }

    return (
        <>
            <IconButton aria-label={t('tableFirstPage')} onClick={() => onSelectPage(1)}>
                <i className='mdi mdi-chevron-double-left' />
            </IconButton>
            <IconButton aria-label={t('tablePreviousPage')} onClick={() => onSelectPage(Math.max(1, page - 1))}>
                <i className='mdi mdi-chevron-left' />
            </IconButton>
            {items}
            <IconButton aria-label={t('tableNextPage')} onClick={() => onSelectPage(Math.min(nrOfPages, page + 1))}>
                <i className='mdi mdi-chevron-right' />
            </IconButton>
            <IconButton aria-label={t('tableLastPage')} onClick={() => onSelectPage(nrOfPages)}>
                <i className='mdi mdi-chevron-double-right' />
            </IconButton>
        </>
    )
}

export default TablePagination
